/**
 * Content Type Registration for Editor
 *
 * Register all available content types from content-elements and content-types
 */
import {
    defineContentType,
    registerContentType,
    getRegistry,
    type ContentTypeDefinition,
} from '@ai-content-hub/content-core';

// Import elements from content-elements
import {
    // Text Element
    textElementSchema,
    TextEdit,
    TextPlayer,
    // Image Element
    imageElementSchema,
    ImageEdit,
    ImagePlayer,
    // Rich Text Element
    richTextElementSchema,
    RichTextEdit,
    RichTextPlayer,
    // Audio Element
    audioElementSchema,
    AudioEdit,
    AudioPlayer,
    // Video Element
    videoElementSchema,
    VideoEdit,
    VideoPlayer,
    // Media Element (unified)
    mediaElementSchema,
    MediaEdit,
    MediaPlayer,
    // MCQ Element
    mcqElementSchema,
    MCQEdit,
    MCQPlayer,
    mcqGrader,
    type MCQElementData,
    type MCQAnswer,
    // Match Element
    matchElementSchema,
    MatchEdit,
    MatchPlayer,
    matchGrader,
    type MatchElementData,
    type MatchAnswer,
    // Color Drop Zone Element
    colorDropZoneElementSchema,
    ColorDropZoneEdit,
    ColorDropZonePlayer,
    colorDropZoneGrader,
    type ColorDropZoneElementData,
    type ColorDropZoneAnswer,
    // Page Element
    pageElementSchema,
    PageEdit,
    PagePlayer,
    // Fill in the Blanks
    fitbElementSchema,
    FITBEdit,
    FITBPlayer,
    fitbGrader,
    defaultFITBConfig,
    type FITBElementData,
    type FITBAnswer,
    // Ordering Element
    orderingElementSchema,
    OrderingEdit,
    OrderingPlayer,
    orderingGrader,
    type OrderingElementData,
    type OrderingAnswer,
    // Record Audio Element
    recordAudioElementSchema,
    RecordAudioEdit,
    RecordAudioPlayer,
} from '@ai-content-hub/content-elements';

// Import composite content types
import {
    mcqSetSchema,
    McqSetEdit,
    MCQSetPlayer,
    defaultMCQSetConfig,
    mcqSetContentGrader,
    VERSION as mcqSetVersion,
} from '@ai-content-hub/content-type-mcq-set';
import {
    questionSetSchema,
    QuestionSetEdit,
    QuestionSetPlayer,
    defaultQuestionSetConfig,
    questionSetContentGrader,
    questionSetValidator,
    VERSION as questionSetVersion,
} from '@ai-content-hub/content-type-question-set';
import {
    qtDropdownSchema,
    DropdownEdit,
    DropdownPlayer,
    defaultQtDropdownConfig,
    qtDropdownContentGrader,
    validateQtDropdownContent,
    VERSION as qtDropdownVersion,
    type QtDropdownData,
} from '@ai-content-hub/content-type-dropdown';
import {
    qtDragTheWordsSchema,
    DragTheWordsEdit,
    DragTheWordsPlayer,
    defaultQtDragTheWordsConfig,
    qtDragTheWordsContentGrader,
    validateQtDragTheWordsContent,
    VERSION as qtDragTheWordsVersion,
    type QtDragTheWordsData,
} from '@ai-content-hub/content-type-drag-the-words';
import {
    speakingSchema,
    SpeakingEdit,
    SpeakingPlayer,
    defaultSpeakingConfig,
    speakingContentGrader,
    speakingValidator,
    VERSION as speakingVersion,
} from '@ai-content-hub/content-type-speaking';
import {
    quizSchema,
    QuizPlayer,
    QuizEdit,
    quizContentGrader,
    quizValidator,
    VERSION as quizVersion,
} from '@ai-content-hub/content-type-quiz';
import {
    lessonMCQSchema,
    LessonMCQEdit,
    LessonMCQPlayer,
    lessonMCQValidator,
    VERSION as lessonMcqVersion,
    type LessonMCQData,
} from '@ai-content-hub/content-type-lesson-mcq';
import {
    richMatchingSchema,
    RichMatchingEdit,
    RichMatchingPlayer,
    richMatchingValidator,
    type RichMatchingData,
    VERSION as richMatchingVersion,
} from '@ai-content-hub/content-type-rich-matching';
import {
    imageHotspotSchema,
    ImageHotspotEdit,
    ImageHotspotPlayer,
    imageHotspotValidator,
    type ImageHotspotData,
    VERSION as imageHotspotVersion,
} from '@ai-content-hub/content-type-image-hotspot';
import {
    essaySchema,
    EssayEdit,
    EssayPlayer,
    essayContentGrader,
    essayValidator,
    VERSION as essayVersion,
} from '@ai-content-hub/content-type-essay';
// EAA-1755: interactive-reading (TTS reading + synced word highlighting + pronunciation grading).
import {
    interactiveReadingSchema,
    InteractiveReadingEdit,
    InteractiveReadingPlayer,
    interactiveReadingContentGrader,
    interactiveReadingValidator,
    defaultInteractiveReadingConfig,
    VERSION as interactiveReadingVersion,
} from '@ai-content-hub/content-type-interactive-reading';
import {
    documentsSchema,
    DocumentsEdit,
    DocumentsPlayer,
    documentsContentGrader,
    documentsValidator,
    VERSION as documentsVersion,
} from '@ai-content-hub/content-type-documents';
// EAA-1642: 3D model content type (GLB/glTF viewer via three.js).
import {
    content3DSchema,
    Content3DEdit,
    Content3DPlayer,
    content3DContentGrader,
    content3DValidator,
    VERSION as content3DVersion,
} from '@ai-content-hub/content-type-3d';
import {
    orderingContentSchema,
    OrderingContentEdit,
    OrderingContentPlayer,
    type OrderingContentData,
    gradeOrderingContentComposite,
    validateOrderingContentComposite,
    VERSION as orderingContentVersion,
} from '@ai-content-hub/content-type-ordering';
import {
    fitbContentSchema,
    FITBContentEdit,
    FITBContentPlayer,
    type FITBContentData,
    gradeFITBContentComposite,
    validateFITBContentComposite,
    VERSION as fitbContentVersion,
} from '@ai-content-hub/content-type-fill-in-the-blanks';
import {
    Scorm12Edit,
    Scorm12Player,
    defaultScorm12Data,
    Scorm2004Edit,
    Scorm2004Player,
    defaultScorm2004Data,
} from '@ai-content-hub/scorm';

/** Content-type package versions, keyed by type slug. */
export const contentTypeVersions: Record<string, string> = {
    'mcq-set': mcqSetVersion,
    'question-set': questionSetVersion,
    'qt-dropdown': qtDropdownVersion,
    'qt-drag-the-words': qtDragTheWordsVersion,
    speaking: speakingVersion,
    quiz: quizVersion,
    'lesson-mcq': lessonMcqVersion,
    'rich-matching': richMatchingVersion,
    'image-hotspot': imageHotspotVersion,
    essay: essayVersion,
    'interactive-reading': interactiveReadingVersion,
    documents: documentsVersion,
    '3d': content3DVersion,
    'ordering-content': orderingContentVersion,
    'fill-in-the-blanks-content': fitbContentVersion,

};

// ============================================
// Define Element Content Types
// ============================================

export const textContentType = defineContentType({
    type: 'text',
    title: textElementSchema.title,
    description: textElementSchema.description,
    version: textElementSchema.version,
    category: 'text',
    fields: textElementSchema.fields as any,
    defaultData: textElementSchema.defaultData,
    EditComponent: TextEdit as any,
    PlayerComponent: TextPlayer as any,
});

export const imageContentType = defineContentType({
    type: 'image',
    title: imageElementSchema.title,
    description: imageElementSchema.description,
    version: imageElementSchema.version,
    category: 'media',
    fields: imageElementSchema.fields as any,
    defaultData: imageElementSchema.defaultData,
    EditComponent: ImageEdit as any,
    PlayerComponent: ImagePlayer as any,
});

export const richTextContentType = defineContentType({
    type: 'rich-text',
    title: richTextElementSchema.title,
    description: richTextElementSchema.description,
    version: richTextElementSchema.version,
    category: 'text',
    fields: richTextElementSchema.fields as any,
    defaultData: richTextElementSchema.defaultData,
    EditComponent: RichTextEdit as any,
    PlayerComponent: RichTextPlayer as any,
});

export const audioContentType = defineContentType({
    type: 'audio',
    title: audioElementSchema.title,
    description: audioElementSchema.description,
    version: audioElementSchema.version,
    category: 'media',
    fields: audioElementSchema.fields as any,
    defaultData: audioElementSchema.defaultData,
    EditComponent: AudioEdit as any,
    PlayerComponent: AudioPlayer as any,
});

export const videoContentType = defineContentType({
    type: 'video',
    title: videoElementSchema.title,
    description: videoElementSchema.description,
    version: videoElementSchema.version,
    category: 'media',
    fields: videoElementSchema.fields as any,
    defaultData: videoElementSchema.defaultData,
    EditComponent: VideoEdit as any,
    PlayerComponent: VideoPlayer as any,
});

export const mediaContentType = defineContentType({
    type: 'media',
    title: mediaElementSchema.title,
    description: mediaElementSchema.description,
    version: mediaElementSchema.version,
    category: 'media',
    fields: mediaElementSchema.fields as any,
    defaultData: mediaElementSchema.defaultData,
    EditComponent: MediaEdit as any,
    PlayerComponent: MediaPlayer as any,
});

export const mcqContentType = defineContentType({
    type: 'mcq',
    title: mcqElementSchema.title,
    description: mcqElementSchema.description,
    version: mcqElementSchema.version,
    category: 'question',
    fields: mcqElementSchema.fields as any,
    defaultData: mcqElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown) => {
            return mcqGrader.grade(data as MCQElementData, answer as MCQAnswer);
        },
    },
    EditComponent: MCQEdit as any,
    PlayerComponent: MCQPlayer as any,
});

export const matchContentType = defineContentType({
    type: 'match',
    title: matchElementSchema.title,
    description: matchElementSchema.description,
    version: matchElementSchema.version,
    category: 'question',
    fields: matchElementSchema.fields as any,
    defaultData: matchElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return matchGrader.grade(data as MatchElementData, answer as MatchAnswer, config);
        },
    },
    EditComponent: MatchEdit as any,
    PlayerComponent: MatchPlayer as any,
});

export const colorDropZoneContentType = defineContentType({
    type: 'color-drop-zone',
    title: colorDropZoneElementSchema.title,
    description: colorDropZoneElementSchema.description,
    version: colorDropZoneElementSchema.version,
    category: 'question',
    fields: colorDropZoneElementSchema.fields as any,
    defaultData: colorDropZoneElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return colorDropZoneGrader.grade(
                data as ColorDropZoneElementData,
                answer as ColorDropZoneAnswer,
                config
            );
        },
    },
    EditComponent: ColorDropZoneEdit as any,
    PlayerComponent: ColorDropZonePlayer as any,
});

export const pageContentType = defineContentType({
    type: 'page',
    title: pageElementSchema.title,
    description: pageElementSchema.description,
    version: pageElementSchema.version,
    category: 'layout' as any,
    fields: pageElementSchema.fields as any,
    defaultData: pageElementSchema.defaultData,
    EditComponent: PageEdit as any,
    PlayerComponent: PagePlayer as any,
});

export const fillInBlanksContentType = defineContentType({
    type: 'fill-in-the-blanks',
    title: fitbElementSchema.title,
    description: fitbElementSchema.description,
    version: fitbElementSchema.version,
    category: 'question',
    fields: fitbElementSchema.fields as any,
    defaultData: fitbElementSchema.defaultData,
    defaultConfig: defaultFITBConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return fitbGrader.grade(data as FITBElementData, answer as FITBAnswer, config);
        },
    },
    validator: {
        validate: (data: unknown) => fitbGrader.validate!(data as FITBElementData),
    },
    EditComponent: FITBEdit as any,
    PlayerComponent: FITBPlayer as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

export const orderingContentType = defineContentType({
    type: 'ordering',
    title: orderingElementSchema.title,
    description: orderingElementSchema.description,
    version: orderingElementSchema.version,
    category: 'question',
    fields: orderingElementSchema.fields as any,
    defaultData: orderingElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return orderingGrader.grade(
                data as OrderingElementData,
                answer as OrderingAnswer,
                config
            );
        },
    },
    EditComponent: OrderingEdit as any,
    PlayerComponent: OrderingPlayer as any,
});

export const recordAudioContentType = defineContentType({
    type: 'record-audio',
    title: recordAudioElementSchema.title,
    description: recordAudioElementSchema.description,
    version: recordAudioElementSchema.version,
    category: 'media',
    fields: recordAudioElementSchema.fields as any,
    defaultData: recordAudioElementSchema.defaultData,
    EditComponent: RecordAudioEdit as any,
    PlayerComponent: RecordAudioPlayer as any,
});

// ============================================
// Define Composite Content Types
// ============================================

// MCQ Set - A collection of MCQ questions
export const mcqSetContentType = defineContentType({
    type: 'mcq-set',
    title: mcqSetSchema.title,
    description: mcqSetSchema.description,
    version: mcqSetSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: mcqSetSchema.defaultData,
    defaultConfig: defaultMCQSetConfig,
    EditComponent: McqSetEdit as any,
    PlayerComponent: MCQSetPlayer as any,
    hasIntegratedConfigTab: true,
    grader: mcqSetContentGrader as any,
});

// Question Set — MCQ, Match, FITB (dropdown / type)
export const questionSetContentType = defineContentType({
    type: 'question-set',
    title: questionSetSchema.title,
    description: questionSetSchema.description,
    version: questionSetSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: questionSetSchema.defaultData,
    defaultConfig: defaultQuestionSetConfig,
    EditComponent: QuestionSetEdit as any,
    PlayerComponent: QuestionSetPlayer as any,
    hasIntegratedConfigTab: true,
    grader: questionSetContentGrader as any,
    validator: questionSetValidator,
    showEditorErrorSummary: false,
});

export const qtDropdownContentType = defineContentType({
    type: 'qt-dropdown',
    title: qtDropdownSchema.title,
    description: qtDropdownSchema.description,
    version: qtDropdownSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: qtDropdownSchema.defaultData,
    defaultConfig: defaultQtDropdownConfig,
    EditComponent: DropdownEdit as any,
    PlayerComponent: DropdownPlayer as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
    grader: qtDropdownContentGrader as any,
    validator: {
        validate: (data: unknown) => validateQtDropdownContent(data as QtDropdownData),
    },
});

export const qtDragTheWordsContentType = defineContentType({
    type: 'qt-drag-the-words',
    title: qtDragTheWordsSchema.title,
    description: qtDragTheWordsSchema.description,
    version: qtDragTheWordsSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: qtDragTheWordsSchema.defaultData,
    defaultConfig: defaultQtDragTheWordsConfig,
    EditComponent: DragTheWordsEdit as any,
    PlayerComponent: DragTheWordsPlayer as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
    grader: qtDragTheWordsContentGrader as any,
    validator: {
        validate: (data: unknown) => validateQtDragTheWordsContent(data as QtDragTheWordsData),
    },
});
export const speakingContentType = defineContentType({
    type: 'speaking',
    title: speakingSchema.title,
    description: speakingSchema.description,
    version: speakingSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: speakingSchema.defaultData,
    defaultConfig: defaultSpeakingConfig,
    EditComponent: SpeakingEdit as any,
    PlayerComponent: SpeakingPlayer as any,
    hasIntegratedConfigTab: true,
    grader: speakingContentGrader as any,
    validator: speakingValidator,
    showEditorErrorSummary: false,
});

// EAA-1755: Interactive Reading — TTS passage with synced word highlighting and
// optional pronunciation auto-grading.
export const interactiveReadingContentType = defineContentType({
    type: 'interactive-reading',
    title: interactiveReadingSchema.title,
    description: interactiveReadingSchema.description,
    version: interactiveReadingSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: interactiveReadingSchema.defaultData,
    defaultConfig: defaultInteractiveReadingConfig,
    EditComponent: InteractiveReadingEdit as any,
    PlayerComponent: InteractiveReadingPlayer as any,
    grader: interactiveReadingContentGrader as any,
    validator: interactiveReadingValidator,
    showEditorErrorSummary: false,
});

// Quiz - A comprehensive assessment with multiple question types
export const quizContentType = defineContentType({
    type: 'quiz',
    title: quizSchema.title,
    description: quizSchema.description,
    version: quizSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: quizSchema.defaultData,
    EditComponent: QuizEdit as any,
    PlayerComponent: QuizPlayer as any,
    grader: quizContentGrader as any,
    validator: quizValidator,
    showEditorErrorSummary: false,
});

// Lesson MCQ - Split-view with lesson content and MCQ questions
export const lessonMCQContentType = defineContentType({
    type: 'lesson-mcq',
    title: lessonMCQSchema.title,
    description: lessonMCQSchema.description,
    version: lessonMCQSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: lessonMCQSchema.defaultData,
    defaultConfig: lessonMCQSchema.defaultConfig,
    EditComponent: LessonMCQEdit as any,
    PlayerComponent: LessonMCQPlayer as any,
    hasIntegratedConfigTab: true,
    grader: {
        grade: (data, answer, scoring, ctx) =>
            mcqSetContentGrader.grade(
                (data as LessonMCQData).questionSet,
                answer as Record<string, MCQAnswer | undefined>,
                scoring,
                ctx
            ),
        validateAnswer: (answer, data, opts) =>
            mcqSetContentGrader.validateAnswer!(
                answer as Record<string, MCQAnswer | undefined>,
                (data as LessonMCQData).questionSet,
                opts
            ),
    } as any,
    validator: lessonMCQValidator,
    showEditorErrorSummary: false,
});

// Rich Matching - Lesson content with matching questions
export const richMatchingContentType = defineContentType({
    type: 'rich-matching',
    title: richMatchingSchema.title,
    description: richMatchingSchema.description,
    version: richMatchingSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: richMatchingSchema.defaultData,
    defaultConfig: richMatchingSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            const richData = data as RichMatchingData;
            return matchGrader.grade(
                richData.matchData as MatchElementData,
                answer as MatchAnswer,
                config
            );
        },
    },
    EditComponent: RichMatchingEdit as any,
    PlayerComponent: RichMatchingPlayer as any,
    hasIntegratedConfigTab: true,
    validator: richMatchingValidator,
    showEditorErrorSummary: false,
});

// Image Hotspot - Image with clickable hotspots that reveal content
export const imageHotspotContentType = defineContentType({
    type: 'image-hotspot',
    title: imageHotspotSchema.title,
    description: imageHotspotSchema.description,
    version: imageHotspotSchema.version,
    category: 'interactive' as any,
    fields: [],
    defaultData: imageHotspotSchema.defaultData,
    defaultConfig: imageHotspotSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            const hotspotData = data as ImageHotspotData;
            return colorDropZoneGrader.grade(
                hotspotData.colorDropZoneData as ColorDropZoneElementData,
                answer as ColorDropZoneAnswer,
                config
            );
        },
    },
    EditComponent: ImageHotspotEdit as any,
    PlayerComponent: ImageHotspotPlayer as any,
    hasIntegratedConfigTab: true,
    validator: imageHotspotValidator,
    showEditorErrorSummary: false,
});

// Essay - Lesson content with essay writing questions (async AI grading via engine Submit)
export const essayContentType = defineContentType({
    type: 'essay',
    title: essaySchema.title,
    description: essaySchema.description,
    version: essaySchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: essaySchema.defaultData,
    defaultConfig: essaySchema.defaultConfig,
    EditComponent: EssayEdit as any,
    PlayerComponent: EssayPlayer as any,
    grader: essayContentGrader as any,
    hasIntegratedConfigTab: true,
    validator: essayValidator,
    showEditorErrorSummary: false,
});

// Documents (Tài liệu) — Office/PDF viewing with completion gates
export const documentsContentType = defineContentType({
    type: 'documents',
    title: documentsSchema.title,
    description: documentsSchema.description,
    version: documentsSchema.version,
    category: documentsSchema.category as any,
    fields: [],
    defaultData: documentsSchema.defaultData,
    defaultConfig: documentsSchema.defaultConfig,
    validator: documentsValidator,
    EditComponent: DocumentsEdit as any,
    PlayerComponent: DocumentsPlayer as any,
    grader: documentsContentGrader as any,
    hasIntegratedConfigTab: true,
    inlinePlayerSubmit: true,
    showEditorErrorSummary: false,
});

// 3D Model — GLB/glTF viewer rendered with three.js (EAA-1642)
export const content3DContentType = defineContentType({
    type: '3d',
    title: content3DSchema.title,
    description: content3DSchema.description,
    version: content3DSchema.version,
    category: content3DSchema.category as any,
    fields: [],
    defaultData: content3DSchema.defaultData,
    defaultConfig: content3DSchema.defaultConfig,
    validator: content3DValidator,
    EditComponent: Content3DEdit as any,
    PlayerComponent: Content3DPlayer as any,
    grader: content3DContentGrader as any,
    hasIntegratedConfigTab: true,
    inlinePlayerSubmit: true,
    showEditorErrorSummary: false,
});

// SCORM 1.2 — Interactive SCORM 1.2 learning content
export const scorm12ContentType = defineContentType({
    type: 'scorm-12',
    title: 'SCORM 1.2',
    description: 'Interactive SCORM 1.2 learning content package',
    version: '1.0.0',
    category: 'layout' as any,
    fields: [],
    defaultData: defaultScorm12Data,
    EditComponent: Scorm12Edit as any,
    PlayerComponent: Scorm12Player as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

// SCORM 2004 — Interactive SCORM 2004 learning content
export const scorm2004ContentType = defineContentType({
    type: 'scorm-2004',
    title: 'SCORM 2004',
    description: 'Interactive SCORM 2004 learning content package',
    version: '1.0.0',
    category: 'layout' as any,
    fields: [],
    defaultData: defaultScorm2004Data,
    EditComponent: Scorm2004Edit as any,
    PlayerComponent: Scorm2004Player as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

// ============================================
// All Content Types
// ============================================

export const elementTypes: ContentTypeDefinition[] = [
    textContentType as any,
    imageContentType as any,
    richTextContentType as any,
    audioContentType as any,
    videoContentType as any,
    mediaContentType as any,
    mcqContentType as any,
    matchContentType as any,
    colorDropZoneContentType as any,
    pageContentType as any,
    fillInBlanksContentType as any,
    orderingContentType as any,
    recordAudioContentType as any,
];

// Ordering Content Type — wraps ordering element with title/description
export const orderingContentTypeComposite = defineContentType({
    type: 'ordering-content',
    title: orderingContentSchema.title,
    description: orderingContentSchema.description,
    version: orderingContentSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: orderingContentSchema.defaultData,
    defaultConfig: orderingContentSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return gradeOrderingContentComposite(
                data as OrderingContentData,
                answer,
                config,
            );
        },
    },
    validator: {
        validate: (data: unknown) => validateOrderingContentComposite(data as OrderingContentData),
    },
    EditComponent: OrderingContentEdit as any,
    PlayerComponent: OrderingContentPlayer as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

// Fill in the Blanks Content Type — wraps FITB element with title/description
export const fitbContentTypeComposite = defineContentType({
    type: 'fill-in-the-blanks-content',
    title: fitbContentSchema.title,
    description: fitbContentSchema.description,
    version: fitbContentSchema.version,
    category: 'assessment' as any,
    fields: [],
    defaultData: fitbContentSchema.defaultData,
    defaultConfig: fitbContentSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return gradeFITBContentComposite(data as FITBContentData, answer, config);
        },
    },
    validator: {
        validate: (data: unknown) => validateFITBContentComposite(data as FITBContentData),
    },
    EditComponent: FITBContentEdit as any,
    PlayerComponent: FITBContentPlayer as any,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

export const compositeTypes: ContentTypeDefinition[] = [
    mcqSetContentType as any,
    questionSetContentType as any,
    qtDropdownContentType as any,
    qtDragTheWordsContentType as any,
    speakingContentType as any,
    interactiveReadingContentType as any,
    quizContentType as any,
    lessonMCQContentType as any,
    richMatchingContentType as any,
    imageHotspotContentType as any,
    essayContentType as any,
    documentsContentType as any,
    content3DContentType as any,
    orderingContentTypeComposite as any,
    fitbContentTypeComposite as any,
];

export const scormTypes: ContentTypeDefinition[] = [
    scorm12ContentType as any,
    scorm2004ContentType as any,
];

export const contentTypes: ContentTypeDefinition[] = [...elementTypes, ...compositeTypes, ...scormTypes];

// Type aliases map (element-X -> X)
const typeAliases: Record<string, ContentTypeDefinition> = {
    'element-text': textContentType as any,
    'element-image': imageContentType as any,
    'element-rich-text': richTextContentType as any,
    'element-audio': audioContentType as any,
    'element-video': videoContentType as any,
    'element-media': mediaContentType as any,
    'element-mcq': mcqContentType as any,
    'element-match': matchContentType as any,
    'element-color-drop-zone': colorDropZoneContentType as any,
    'element-fill-in-the-blanks': fillInBlanksContentType as any,
    'element-ordering': orderingContentType as any,
    'element-record-audio': recordAudioContentType as any,
};

// ============================================
// Register All Content Types
// ============================================

let registered = false;

export function registerAllContentTypes(): void {
    if (registered) return;

    console.log('[content-types] Registering content types...');
    console.log(
        '[content-types] Element types:',
        elementTypes.map((t) => t.schema.type)
    );
    console.log(
        '[content-types] Composite types:',
        compositeTypes.map((t) => t.schema.type)
    );

    // Register primary content types
    contentTypes.forEach((type) => {
        console.log(`[content-types] Registering: ${type.schema.type}`);
        registerContentType(type);
    });

    // Register aliases (element-X -> X)
    Object.entries(typeAliases).forEach(([aliasType, contentType]) => {
        const aliasedType = {
            ...contentType,
            schema: {
                ...contentType.schema,
                type: aliasType,
            },
        };
        registerContentType(aliasedType);
    });

    registered = true;

    // Verify registration
    const registeredTypes = getRegistry().getTypes();
    console.log('[content-types] Registered types in registry:', registeredTypes);
    console.log(
        `[content-types] Registered ${contentTypes.length} content types (${elementTypes.length} elements + ${compositeTypes.length} composites)`
    );
}

// ============================================
// Get Content Type Info
// ============================================

export function getContentTypeList() {
    return contentTypes.map((type) => ({
        type: type.schema.type,
        title: type.schema.title,
        description: type.schema.description,
        category: type.schema.category,
    }));
}

export function getContentType(type: string) {
    const result = getRegistry().get(type);
    console.log(
        `[content-types] getContentType("${type}") =>`,
        result ? `found (${result.type})` : 'NOT FOUND'
    );
    if (!result) {
        console.log('[content-types] Available types:', getRegistry().getTypes());
    }
    return result;
}
